import { useRef, useState } from "react";
import { Link } from "@/i18n/LocaleLink";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { CONTACT, useContent } from "@/data/content";
import { MENOPAUSE_SUPPORT } from "@/data/menopauseSupport";
import { useLang } from "@/i18n/LanguageContext";
import { useSeo } from "@/hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CallButton = ({ label, testid }) => (
  <a
    href={CONTACT.introCallUrl}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className="group inline-flex items-center gap-3 rounded-full bg-clay px-8 py-4 text-sm tracking-wide text-cream transition-colors duration-300 hover:bg-rust"
  >
    {label}
    <ArrowUpRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </a>
);

const Hero = ({ mn }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden grain" data-testid="mn-hero">
      <motion.div style={{ y: imgY }} className="absolute inset-0 -bottom-32">
        <img src="/images/menopause.jpg" alt="A woman pausing by a window in quiet natural light" className="h-full w-full object-cover scale-105" />
        <div className="absolute inset-0 bg-ink/40" />
        <div className="absolute inset-x-0 bottom-0 h-72 bg-gradient-to-t from-ink/70 to-transparent" />
      </motion.div>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col justify-end px-6 pb-20 pt-40 lg:px-10">
        <div className="overflow-hidden">
          <motion.h1
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.1, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif text-cream text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl"
            data-testid="mn-hero-title"
          >
            {mn.hero.title}
          </motion.h1>
        </div>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.7 }}
          className="mt-7 max-w-2xl font-serif italic text-2xl leading-snug text-sand"
          data-testid="mn-hero-sub"
        >
          {mn.hero.sub}
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85 }}
          className="mt-5 max-w-2xl text-base sm:text-lg leading-relaxed text-cream"
          data-testid="mn-hero-intro"
        >
          {mn.hero.intro}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1 }}
          className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm tracking-wide text-sand"
          data-testid="mn-hero-facts"
        >
          {mn.hero.facts.map((f, i) => (
            <span key={f} className="flex items-center gap-5">
              {i > 0 && <span className="text-cream/40">·</span>}
              <span>{f}</span>
            </span>
          ))}
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15 }}
          className="mt-9 flex flex-wrap items-center gap-6"
        >
          <CallButton label={mn.hero.primaryCta} testid="mn-hero-call" />
          <a href="#format" data-testid="mn-hero-more"
            className="group inline-flex items-center gap-2 text-sm tracking-wide text-cream/85 hover:text-cream transition-colors duration-300 border-b border-cream/40 pb-1">
            {mn.hero.secondaryCta}
            <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

const FaqItem = ({ item, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-ink/10" data-testid={`mn-faq-${index}`}>
      <button onClick={() => setOpen(!open)} aria-expanded={open} className="flex w-full items-center justify-between gap-6 py-5 text-left" data-testid={`mn-faq-toggle-${index}`}>
        <span className="font-serif text-lg sm:text-xl text-ink">{item.q}</span>
        <ChevronDown size={18} strokeWidth={1.5} className={`shrink-0 text-clay transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }} className="overflow-hidden">
            <p className="pb-6 pr-10 text-base leading-relaxed text-soft">{item.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function MenopauseSupportPage() {
  const { lang } = useLang();
  const { offerings } = useContent();
  const mn = MENOPAUSE_SUPPORT[lang] || MENOPAUSE_SUPPORT.en;
  useSeo({ ...mn.seo, image: `/images/menopause.jpg` });
  const topicName = offerings.find((o) => o.slug === "menopause-support")?.name || "Perimenopause / Menopause 1:1 Support";
  const contactLink = `/contact?topic=${encodeURIComponent(topicName)}`;
  const [buying, setBuying] = useState(false);
  const buyProgramme = async () => {
    setBuying(true);
    try {
      const res = await fetch(`${API}/programme/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lang, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "checkout failed");
      window.location.href = data.checkout_url;
    } catch {
      setBuying(false);
      toast.error(
        lang === "nl"
          ? "Er ging iets mis bij het starten van de betaling — probeer het opnieuw of schrijf me even."
          : "Something went wrong starting checkout — please try again or write to me."
      );
    }
  };

  return (
    <main data-testid="menopause-support-page">
      <Hero mn={mn} />

      <section id="format" className="bg-parchment border-b border-ink/10 py-16" data-testid="mn-format">
        <div className="mx-auto max-w-4xl px-6">
          <Reveal>
            <p className="text-xs tracking-[0.3em] uppercase text-clay mb-5">{mn.format.heading}</p>
            <ul className="space-y-2 text-base leading-relaxed text-soft" data-testid="mn-format-rows">
              {mn.format.rows.map((r) => <li key={r}>{r}</li>)}
            </ul>
            <p className="mt-6 font-serif italic text-lg text-ink/75" data-testid="mn-format-investment">{mn.format.investment}</p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button type="button" onClick={buyProgramme} disabled={buying} data-testid="mn-format-buy"
                className="group inline-flex items-center gap-3 rounded-full bg-clay px-8 py-4 text-sm tracking-wide text-cream transition-colors duration-300 hover:bg-rust disabled:opacity-60 disabled:cursor-wait">
                {buying ? "…" : mn.format.buyCta}
                {!buying && <ArrowUpRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />}
              </button>
              <a href={CONTACT.introCallUrl} target="_blank" rel="noopener noreferrer" data-testid="mn-format-call"
                className="group inline-flex items-center gap-3 rounded-full border border-clay px-8 py-4 text-sm tracking-wide text-clay transition-colors duration-300 hover:bg-clay hover:text-cream">
                {mn.format.cta}
                <ArrowUpRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
            <Link to={contactLink} data-testid="mn-format-write"
              className="group mt-6 inline-flex items-center gap-2 text-sm text-soft hover:text-ink transition-colors duration-300 border-b border-ink/20 pb-0.5">
              {mn.format.writeCta}
              <ArrowRight size={14} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <p className="mt-8 max-w-xl text-xs leading-relaxed text-soft/80" data-testid="mn-format-withdraw-note">
              {mn.format.withdrawNote}{" "}
              <Link to="/withdrawal" data-testid="mn-format-withdraw-link" className="text-clay border-b border-clay/40 pb-0.5 hover:text-rust hover:border-rust transition-colors duration-300">
                {mn.format.withdrawLink}
              </Link>
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-24 lg:py-36" data-testid="mn-recognition">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.2] tracking-tight text-ink">{mn.recognition.title}</h2>
            <p className="mt-8 text-base leading-relaxed text-soft drop-cap">{mn.recognition.p1}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{mn.recognition.p2}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{mn.recognition.p3}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{mn.recognition.p4}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-12 font-serif text-2xl sm:text-3xl leading-[1.3] tracking-tight text-clay text-center" data-testid="mn-recognition-pull">{mn.recognition.pull}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-24 lg:py-32" data-testid="mn-happens">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{mn.happens.title}</h2>
            <p className="mt-7 text-base leading-relaxed text-soft">{mn.happens.p1}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{mn.happens.p2}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{mn.happens.p3}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{mn.happens.p4}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{mn.happens.p5}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-12 font-serif italic text-2xl leading-snug text-ink/80 border-l-2 border-clay/40 pl-8" data-testid="mn-happens-pull">{mn.happens.pull}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-16 lg:py-20" data-testid="mn-testimonials">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{mn.testimonials.heading}</h2>
            <div className="mt-12 text-left max-w-2xl mx-auto">
              {mn.testimonials.items.map((t, i) => (
                <blockquote key={i} data-testid={`mn-testimonial-${i}`}>
                  <p className="font-serif italic text-5xl leading-none text-clay/40" aria-hidden>"</p>
                  <p className="mt-2 font-serif italic text-lg leading-relaxed text-ink/85">{t.text}</p>
                  <footer className="mt-5 text-xs tracking-[0.25em] uppercase text-clay">— {t.name}</footer>
                </blockquote>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-24 lg:py-32" data-testid="mn-why4">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{mn.why4.title}</h2>
            <p className="mt-7 text-base leading-relaxed text-soft">{mn.why4.p1}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{mn.why4.p2}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{mn.why4.p3}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-14 flex flex-wrap items-baseline justify-center gap-x-4 gap-y-2 text-center font-serif text-2xl sm:text-3xl tracking-tight text-clay" data-testid="mn-rhythm">
              {mn.why4.rhythm.flatMap((w, i) =>
                i === 0
                  ? [<span key={w}>{w}</span>]
                  : [<span key={`sep-${i}`} className="text-clay/40">·</span>, <span key={w}>{w}</span>]
              )}
            </p>
            <p className="mt-6 text-center text-sm italic text-soft">{mn.why4.closing}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-24 lg:py-32" data-testid="mn-readings">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl leading-snug tracking-tight text-ink">{mn.readings.title}</h2>
            <p className="mt-7 text-base leading-relaxed text-soft">{mn.readings.p1}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{mn.readings.p2}</p>
          </Reveal>
          <div className="mt-10">
            {mn.readings.items.map((r, i) => (
              <Reveal key={r.title} delay={i * 0.05}>
                <div className="border-t border-ink/10 py-7" data-testid={`mn-reading-${i}`}>
                  <p className="font-serif text-xl sm:text-2xl leading-snug text-ink">{r.title}</p>
                  <p className="mt-2 text-sm leading-relaxed text-soft">{r.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p className="mt-10 font-serif italic text-xl leading-snug text-clay" data-testid="mn-readings-pull">{mn.readings.pull}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-24 lg:py-36" data-testid="mn-deeper">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.2] tracking-tight text-ink">{mn.deeper.title}</h2>
            <p className="mt-8 text-base leading-relaxed text-soft">{mn.deeper.p1}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{mn.deeper.p2}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{mn.deeper.p3}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 space-y-4" data-testid="mn-deeper-questions">
              {mn.deeper.questions.map((q, i) => (
                <p key={i} className="font-serif text-xl sm:text-2xl leading-snug text-clay/90 italic border-l-2 border-clay/30 pl-6">{q}</p>
              ))}
            </div>
            <p className="mt-10 text-base leading-relaxed text-soft">{mn.deeper.p4}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{mn.deeper.p5}</p>
            <p className="mt-12 font-serif text-2xl sm:text-3xl leading-[1.3] tracking-tight text-ink/85" data-testid="mn-deeper-pull">{mn.deeper.pull}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-16 lg:py-20" data-testid="mn-medical">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-ink">{mn.medical.title}</h2>
            <p className="mt-6 text-base leading-relaxed text-soft">{mn.medical.p1}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{mn.medical.p2}</p>
            <p className="mt-4 text-sm leading-relaxed text-soft">{mn.medical.p3}</p>
            <p className="mt-4 text-sm leading-relaxed text-soft">{mn.medical.p4}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-20 lg:py-28" data-testid="mn-irida">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <div className="relative">
              <div className="absolute -inset-4 rounded-t-[10rem] border border-clay/30" aria-hidden />
              <div className="overflow-hidden rounded-t-[10rem]">
                <img loading="lazy" src="/irida.jpg" alt="Irida Hysenbelli, founder of Women's Den" className="aspect-[3/4] w-full object-cover" data-testid="mn-irida-photo" />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7 lg:col-start-6">
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{mn.irida.heading}</h2>
            <p className="mt-6 text-base leading-relaxed text-soft drop-cap">{mn.irida.body}</p>
            <Link to="/about" data-testid="mn-irida-link"
              className="group mt-8 inline-flex items-center gap-3 border-b border-clay pb-1 text-sm tracking-wide text-clay transition-colors duration-300 hover:border-rust hover:text-rust">
              {mn.irida.link}
              <ArrowUpRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-16 lg:py-24" data-testid="mn-faq">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink mb-6">{mn.faq.heading}</h2>
          </Reveal>
          <Reveal>
            <div>
              {mn.faq.items.map((item, i) => <FaqItem key={i} item={item} index={i} />)}
            </div>
          </Reveal>
        </div>
      </section>

    </main>
  );
}
